import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, ShieldCheck } from 'lucide-react';
import { apiService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import PublicShell from './public/PublicShell';
import { getLastEventNameId, EVENTS_LIST_PATH } from '../config/events';
import './TermsOfService.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

const TOS_STEPS = [
    { label: 'Sign in', description: 'Done — you’re verified.' },
    { label: 'Accept the terms', description: 'A one-time step before you enter the event.' },
    { label: 'Enter the event', description: 'Your home page, profile and connections.' },
];

const TermsOfService = () => {
    const navigate = useNavigate();
    const { updateTermsAcceptance } = useAuth();
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [accepting, setAccepting] = useState(false);
    const [agreed, setAgreed] = useState(false);
    const [event, setEvent] = useState(null);
    const [loadingMessage, setLoadingMessage] = useState('Loading Terms of Service...');
    const eventNameId = getLastEventNameId();

    useEffect(() => {
        waitForIcJwtAndFetchContent();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Event summary for the brand panel (non-fatal if unavailable)
    useEffect(() => {
        let cancelled = false;
        axios.get(`${API_BASE_URL}/public/events/${eventNameId}`)
            .then((res) => { if (!cancelled) setEvent(res.data); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [eventNameId]);

    // Helper function to delay
    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    // Wait for ic_jwt to be ready, then fetch content
    const waitForIcJwtAndFetchContent = async (maxWaitAttempts = 15, pollInterval = 1000) => {
        setLoading(true);
        setError('');

        // First, poll for ic_jwt readiness
        for (let attempt = 1; attempt <= maxWaitAttempts; attempt++) {
            try {
                setLoadingMessage(
                    attempt === 1 
                        ? 'Preparing authentication...' 
                        : `Waiting for authentication to complete... (${attempt}/${maxWaitAttempts})`
                );

                const readyResponse = await apiService.checkIcJwtReady();

                if (readyResponse.data.ready) {
                    console.log('ic_jwt is ready, fetching TOS content');
                    break; // ic_jwt is ready, proceed to fetch content
                }
                
                if (attempt === maxWaitAttempts) {
                    console.warn('ic_jwt not ready after max wait attempts');
                    setError('Authentication is taking longer than expected. Please try refreshing the page or logging in again.');
                    setLoading(false);
                    return;
                }
                
                console.log(`ic_jwt not ready, waiting ${pollInterval}ms (attempt ${attempt}/${maxWaitAttempts})`);
                await delay(pollInterval);
                
            } catch (err) {
                console.error(`Error checking ic_jwt readiness (attempt ${attempt}):`, err);

                // If it's a network error or server error, keep trying
                if (attempt < maxWaitAttempts) {
                    await delay(pollInterval);
                    continue;
                }

                setError('Failed to verify authentication. Please try again.');
                setLoading(false);
                return;
            }
        }

        // Now fetch the actual TOS content
        setLoadingMessage('Loading Terms of Service...');
        await fetchTermsContent();
    };

    // Fetch the TOS content (called after ic_jwt is confirmed ready)
    const fetchTermsContent = async () => {
        try {
            const response = await apiService.getTermsOfServiceContent();

            if (response.data.success) {
                setContent(response.data.content);
            } else {
                setError(response.data.error || 'Failed to load terms of service');
            }
        } catch (err) {
            console.error('Error fetching terms of service:', err);
            setError('Failed to load terms of service. Please try again later.');
        } finally {
            setLoading(false);
        }
    };

    // Retry handler - goes through full flow again
    const handleRetry = () => {
        waitForIcJwtAndFetchContent();
    };

    const handleAccept = async () => {
        try {
            setAccepting(true);
            const response = await apiService.acceptTermsOfService();

            if (response.data.success) {
                // Update the auth context
                updateTermsAcceptance(true);

                // Redirect to the last event, or the list of events when none is known
                const lastEvent = localStorage.getItem('last_event');
                if (lastEvent) {
                    navigate(`/event/${lastEvent}/home`);
                } else {
                    navigate(EVENTS_LIST_PATH);
                }
            } else {
                setError('Failed to accept terms of service. Please try again.');
            }
        } catch (err) {
            console.error('Error accepting terms of service:', err);
            setError('Failed to accept terms of service. Please try again.');
        } finally {
            setAccepting(false);
        }
    };

    return (
        <PublicShell
            event={event}
            logoSrc="/Funds4Charity.png"
            steps={TOS_STEPS}
            currentStep={1}
            stepsTitle="Almost there"
            eyebrow="One last step"
            title="Terms of Service"
            subtitle="Please review and accept the terms to continue to the event."
            wide
            className="tos-page"
            footer={<>Not you? <Link to="/logout">Sign out</Link></>}
        >
            {loading && (
                <div className="tos-loading" role="status">
                    <span className="pshell-spinner" aria-hidden="true" />
                    <span>{loadingMessage}</span>
                </div>
            )}

            {!loading && error && (
                <div className="pshell-alert error" role="alert">
                    <AlertCircle size={16} aria-hidden="true" />
                    <div>
                        {error}{' '}
                        <button type="button" className="pshell-inline-btn" onClick={handleRetry}>Try again</button>
                    </div>
                </div>
            )}

            {!loading && !error && content && (
                <>
                    <div
                        className="tos-content"
                        tabIndex={0}
                        dangerouslySetInnerHTML={{ __html: content }}
                    />

                    <label className={`tos-agree ${agreed ? 'checked' : ''}`}>
                        <input
                            type="checkbox"
                            checked={agreed}
                            onChange={(e) => setAgreed(e.target.checked)}
                            disabled={accepting}
                        />
                        <ShieldCheck size={18} aria-hidden="true" className="tos-agree-icon" />
                        <span>I have read and agree to the Terms of Service.</span>
                    </label>

                    <div className="pshell-actions end">
                        <button
                            type="button"
                            className="pshell-btn primary"
                            onClick={handleAccept}
                            disabled={accepting || !agreed}
                        >
                            {accepting ? 'Saving…' : 'Accept and continue'}
                        </button>
                    </div>
                </>
            )}
        </PublicShell>
    );
};

export default TermsOfService;
